import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Dimensions,
  ActivityIndicator,
  Alert,
  Modal,
  Animated,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  RefreshControl,
  FlatList,
  Image,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import {
  Ionicons,
  MaterialIcons,
  FontAwesome5,
  MaterialCommunityIcons,
  AntDesign,
  Feather,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import CareerCoachingService from '../../services/CareerCoachingService';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

export default function CareerCoachingScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_careercoachingscreen`;
    apiFetch('/api/data/customers?sort=created_at&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();
  const route = useRoute();

  // Main states
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);

  // Coaching states
  const [currentSession, setCurrentSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [goals, setGoals] = useState([]);
  const [progressData, setProgressData] = useState({});

  // Interview preparation states
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [interviewFeedback, setInterviewFeedback] = useState(null);
  const [practiceMode, setPracticeMode] = useState(false);
  const [interviewHistory, setInterviewHistory] = useState([]);

  // Learning resources states
  const [learningResources, setLearningResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);

  // Modal states
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);

  // Form states
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [newGoalCategory, setNewGoalCategory] = useState('career_planning');
  const [newGoalType, setNewGoalType] = useState('short_term');

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;

  // Chat scroll ref
  const chatScrollRef = useRef(null);

  // Initialize coaching system
  useEffect(() => {
    initializeCoaching();
  }, []);

  // Refresh data when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      loadCoachingData();
    }, [])
  );

  const initializeCoaching = async () => {
    try {
      setIsLoading(true);
      
      // Initialize the career coaching service
      await CareerCoachingService.initialize();
      
      // Load all coaching data
      await loadCoachingData();
      
      // Start animations
      startAnimations();
      
      setIsLoading(false);
    } catch (error) {
      console.error('❌ Coaching initialization failed:', error);
      Alert.alert('Error', 'Failed to initialize career coaching. Please try again.');
      setIsLoading(false);
    }
  };

  const loadCoachingData = async () => {
    try {
      // Load all coaching data in parallel
      const [
        analyticsData,
        userGoals,
        progressTracking,
        resources,
        history,
        questions
      ] = await Promise.all([
        CareerCoachingService.generateCoachingAnalytics(),
        CareerCoachingService.getUserGoals(),
        CareerCoachingService.getProgressTracking(),
        CareerCoachingService.getLearningResources(),
        CareerCoachingService.getInterviewHistory(),
        CareerCoachingService.getInterviewQuestions()
      ]);

      setAnalytics(analyticsData);
      setGoals(userGoals);
      setProgressData(progressTracking);
      setLearningResources(resources);
      setInterviewHistory(history);
      setInterviewQuestions(questions);

      console.log('✅ Coaching data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading coaching data:', error);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // Refresh handler
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadCoachingData();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // AI Chat functionality
  const startNewChatSession = async () => {
    try {
      const session = await CareerCoachingService.createCoachingSession({
        type: 'ai_chat',
        title: 'AI Coaching Session',
        category: 'general'
      });
      
      setCurrentSession(session);
      
      // Initialize with welcome message
      const welcomeMessage = {
        id: `msg_${Date.now()}`,
        type: 'ai',
        content: `Hello! I'm your AI Career Coach. I'm here to help you with career guidance, interview preparation, resume optimization, and achieving your professional goals. How can I assist you today?`,
        timestamp: new Date().toISOString(),
        suggestions: [
          'Help me prepare for interviews',
          'Analyze my career goals',
          'Create development plan',
          'Practice interview questions'
        ]
      };
      
      setChatMessages([welcomeMessage]);
      setShowChatModal(true);
    } catch (error) {
      console.error('❌ Error starting chat session:', error);
      Alert.alert('Error', 'Failed to start coaching session. Please try again.');
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim() || !currentSession) return;

    try {
      setIsTyping(true);
      
      // Send message to AI and get response
      const { userMessage, aiResponse } = await CareerCoachingService.sendMessageToAI(
        currentSession.id,
        message,
        { goals, progressData }
      );
      
      // Update chat messages
      setChatMessages(prev => [...prev, userMessage, aiResponse]);
      setCurrentMessage('');
      
      // Scroll to bottom
      setTimeout(() => {
        chatScrollRef.current?.scrollToEnd({ animated: true });
      }, 100);
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      Alert.alert('Error', 'Failed to send message. Please try again.');
    } finally {
      setIsTyping(false);
    }
  };

  const handleSuggestionPress = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleActionItemPress = (actionItem) => {
    switch (actionItem.type) {
      case 'start_interview_practice':
        setShowChatModal(false);
        startInterviewPractice();
        break;
      case 'create_career_plan':
        setShowChatModal(false);
        setShowGoalModal(true);
        break;
      case 'skill_assessment':
        setShowChatModal(false);
        // Navigate to skill assessment
        break;
      case 'view_resources':
        setShowChatModal(false);
        setActiveTab('resources');
        break;
      default:
        console.log('Unknown action:', actionItem.type);
    }
  };

  // Goal management
  const createNewGoal = async () => {
    if (!newGoalTitle.trim()) {
      Alert.alert('Error', 'Please enter a goal title.');
      return;
    }

    try {
      const goalData = {
        title: newGoalTitle,
        description: newGoalDescription,
        category: newGoalCategory,
        type: newGoalType,
        target: 100,
        targetDate: new Date(Date.now() + (newGoalType === 'short_term' ? 90 : newGoalType === 'medium_term' ? 365 : 1825) * 24 * 60 * 60 * 1000).toISOString(),
        milestones: []
      };

      await CareerCoachingService.createUserGoal(goalData);
      
      // Reset form
      setNewGoalTitle('');
      setNewGoalDescription('');
      setNewGoalCategory('career_planning');
      setNewGoalType('short_term');
      setShowGoalModal(false);
      
      // Reload data
      await loadCoachingData();
      
      Alert.alert('Success', 'Goal created successfully!');
    } catch (error) {
      console.error('❌ Error creating goal:', error);
      Alert.alert('Error', 'Failed to create goal. Please try again.');
    }
  };

  const updateGoalProgress = async (goalId, progress) => {
    try {
      await CareerCoachingService.updateGoalProgress(goalId, progress);
      await loadCoachingData();
    } catch (error) {
      console.error('❌ Error updating goal progress:', error);
      Alert.alert('Error', 'Failed to update progress. Please try again.');
    }
  };

  // Interview practice
  const startInterviewPractice = async () => {
    try {
      const questions = await CareerCoachingService.getInterviewQuestions();
      setInterviewQuestions(questions);
      setCurrentQuestion(0);
      setUserAnswer('');
      setPracticeMode(true);
      setShowInterviewModal(true);
    } catch (error) {
      console.error('❌ Error starting interview practice:', error);
      Alert.alert('Error', 'Failed to start interview practice. Please try again.');
    }
  };

  const submitAnswer = async () => {
    if (!userAnswer.trim()) {
      Alert.alert('Error', 'Please provide an answer before continuing.');
      return;
    }

    try {
      // Save the answer (in a real app, this might be analyzed by AI)
      const sessionData = {
        questionId: interviewQuestions[currentQuestion].id,
        question: interviewQuestions[currentQuestion].question,
        answer: userAnswer,
        timestamp: new Date().toISOString()
      };

      // Move to next question or finish
      if (currentQuestion < interviewQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setUserAnswer('');
      } else {
        // Finish practice session
        await CareerCoachingService.saveInterviewPracticeSession({
          questions: interviewQuestions,
          answers: [sessionData], // In real app, collect all answers
          completedAt: new Date().toISOString(),
          score: Math.floor(Math.random() * 30) + 70 // Mock score
        });
        
        setPracticeMode(false);
        setShowInterviewModal(false);
        Alert.alert('Great Job!', 'Interview practice completed. Keep practicing to improve your skills!');
        
        // Reload data to update progress
        await loadCoachingData();
      }
    } catch (error) {
      console.error('❌ Error submitting answer:', error);
      Alert.alert('Error', 'Failed to submit answer. Please try again.');
    }
  };

  // Render functions
  const renderHeader = () => (
    <LinearGradient colors={['#667eea', '#764ba2']} style={styles.header}>
      <View style={styles.headerContent}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="white" />
        </TouchableOpacity>
        
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Career Coaching</Text>
          <Text style={styles.headerSubtitle}>Your AI-Powered Career Guide</Text>
        </View>
        
        <TouchableOpacity
          style={styles.chatButton}
          onPress={startNewChatSession}
        >
          <Ionicons name="chatbubble-ellipses" size={24} color="white" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabContainer}>
      {[
        { key: 'dashboard', label: 'Dashboard', icon: 'home' },
        { key: 'goals', label: 'Goals', icon: 'flag' },
        { key: 'practice', label: 'Practice', icon: 'school' },
        { key: 'resources', label: 'Resources', icon: 'library' },
      ].map((tab) => (
        <TouchableOpacity
          key={tab.key}
          style={[styles.tab, activeTab === tab.key && styles.activeTab]}
          onPress={() => setActiveTab(tab.key)}
        >
          <Ionicons
            name={tab.icon}
            size={20}
            color={activeTab === tab.key ? '#667eea' : '#666'}
          />
          <Text
            style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText,
            ]}
          >
            {tab.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderDashboard = () => (
    <ScrollView 
      style={styles.tabContent}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Analytics Overview */}
      {analytics && (
        <Animated.View 
          style={[
            styles.section,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }]
            }
          ]}
        >
          <Text style={styles.sectionTitle}>Your Progress Overview</Text>
          <View style={styles.analyticsGrid}>
            <View style={styles.analyticsCard}>
              <View style={styles.analyticsIconContainer}>
                <Ionicons name="flag" size={24} color="#667eea" />
              </View>
              <Text style={styles.analyticsNumber}>{analytics.overview.totalGoals}</Text>
              <Text style={styles.analyticsLabel}>Total Goals</Text>
            </View>
            
            <View style={styles.analyticsCard}>
              <View style={styles.analyticsIconContainer}>
                <Ionicons name="trending-up" size={24} color="#27ae60" />
              </View>
              <Text style={styles.analyticsNumber}>{analytics.overview.completedGoals}</Text>
              <Text style={styles.analyticsLabel}>Completed</Text>
            </View>
            
            <View style={styles.analyticsCard}>
              <View style={styles.analyticsIconContainer}>
                <Ionicons name="time" size={24} color="#f39c12" />
              </View>
              <Text style={styles.analyticsNumber}>{analytics.overview.activeSessions}</Text>
              <Text style={styles.analyticsLabel}>Active Sessions</Text>
            </View>
            
            <View style={styles.analyticsCard}>
              <View style={styles.analyticsIconContainer}>
                <Ionicons name="trophy" size={24} color="#e74c3c" />
              </View>
              <Text style={styles.analyticsNumber}>{analytics.progress.overallProgress}%</Text>
              <Text style={styles.analyticsLabel}>Overall Progress</Text>
            </View>
          </View>
        </Animated.View>
      )}

      {/* Quick Actions */}
      <Animated.View 
        style={[
          styles.section,
          {
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }]
          }
        ]}
      >
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity 
            style={styles.quickActionCard}
            onPress={startNewChatSession}
          >
            <LinearGradient
              colors={['#667eea', '#764ba2']}
              style={styles.quickActionGradient}
            >
              <Ionicons name="chatbubble-ellipses" size={28} color="white" />
              <Text style={styles.quickActionText}>AI Coach Chat</Text>
            </LinearGradient>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionCard}
            onPress={startInterviewPractice}
          >
            <LinearGradient
              colors={['#11998e', '#38ef7d']}
              style={styles.quickActionGradient}
            >
              <Ionicons name="school" size={28} color="white" />
              <Text style={styles.quickActionText}>Interview Practice</Text>
            </LinearGradient>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionCard}
            onPress={() => setShowGoalModal(true)}
          >
            <LinearGradient
              colors={['#ff9a9e', '#fecfef']}
              style={styles.quickActionGradient}
            >
              <Ionicons name="flag" size={28} color="white" />
              <Text style={styles.quickActionText}>Set New Goal</Text>
            </LinearGradient>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.quickActionCard}
            onPress={() => setActiveTab('resources')}
          >
            <LinearGradient
              colors={['#ffecd2', '#fcb69f']}
              style={styles.quickActionGradient}
            >
              <Ionicons name="library" size={28} color="white" />
              <Text style={styles.quickActionText}>Learning Resources</Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </Animated.View>

      {/* Recent Goals */}
      {goals.length > 0 && (
        <Animated.View 
          style={[
            styles.section,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }]
            }
          ]}
        >
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Recent Goals</Text>
            <TouchableOpacity onPress={() => setActiveTab('goals')}>
              <Text style={styles.seeAllText}>See All</Text>
            </TouchableOpacity>
          </View>
          
          {goals.slice(0, 3).map((goal) => (
            <View key={goal.id} style={styles.goalCard}>
              <View style={styles.goalHeader}>
                <Text style={styles.goalTitle}>{goal.title}</Text>
                <Text style={styles.goalProgress}>{Math.round((goal.progress / goal.target) * 100)}%</Text>
              </View>
              <Text style={styles.goalDescription}>{goal.description}</Text>
              <View style={styles.goalProgressBar}>
                <View 
                  style={[
                    styles.goalProgressFill, 
                    { width: `${(goal.progress / goal.target) * 100}%` }
                  ]} 
                />
              </View>
              <View style={styles.goalFooter}>
                <Text style={styles.goalCategory}>{goal.category.replace('_', ' ').toUpperCase()}</Text>
                <Text style={styles.goalType}>{goal.type.replace('_', ' ')}</Text>
              </View>
            </View>
          ))}
        </Animated.View>
      )}

      {/* Recommendations */}
      {analytics?.recommendations && analytics.recommendations.length > 0 && (
        <Animated.View 
          style={[
            styles.section,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }]
            }
          ]}
        >
          <Text style={styles.sectionTitle}>Recommendations for You</Text>
          {analytics.recommendations.map((rec, index) => (
            <View key={index} style={styles.recommendationCard}>
              <View style={styles.recommendationHeader}>
                <Ionicons 
                  name={rec.priority === 'high' ? 'alert-circle' : 'information-circle'} 
                  size={20} 
                  color={rec.priority === 'high' ? '#e74c3c' : '#3498db'} 
                />
                <Text style={styles.recommendationTitle}>{rec.title}</Text>
              </View>
              <Text style={styles.recommendationDescription}>{rec.description}</Text>
              <TouchableOpacity 
                style={styles.recommendationButton}
                onPress={() => {
                  // Handle recommendation action
                  if (rec.action === 'create_goal') setShowGoalModal(true);
                  else if (rec.action === 'start_session') startNewChatSession();
                }}
              >
                <Text style={styles.recommendationButtonText}>Take Action</Text>
              </TouchableOpacity>
            </View>
          ))}
        </Animated.View>
      )}
    </ScrollView>
  );

  const renderGoals = () => (
    <ScrollView style={styles.tabContent}>
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Your Career Goals</Text>
          <TouchableOpacity 
            style={styles.addButton}
            onPress={() => setShowGoalModal(true)}
          >
            <Ionicons name="add" size={20} color="white" />
          </TouchableOpacity>
        </View>
        
        {goals.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="flag-outline" size={64} color="#bdc3c7" />
            <Text style={styles.emptyStateTitle}>No Goals Yet</Text>
            <Text style={styles.emptyStateDescription}>
              Set your first career goal to start your coaching journey
            </Text>
            <TouchableOpacity 
              style={styles.emptyStateButton}
              onPress={() => setShowGoalModal(true)}
            >
              <Text style={styles.emptyStateButtonText}>Create Your First Goal</Text>
            </TouchableOpacity>
          </View>
        ) : (
          goals.map((goal) => (
            <TouchableOpacity
              key={goal.id}
              style={styles.goalCard}
              onPress={() => {
                setSelectedGoal(goal);
                setShowProgressModal(true);
              }}
            >
              <View style={styles.goalHeader}>
                <Text style={styles.goalTitle}>{goal.title}</Text>
                <View style={styles.goalStatusBadge}>
                  <Text style={styles.goalStatusText}>{goal.status}</Text>
                </View>
              </View>
              
              <Text style={styles.goalDescription}>{goal.description}</Text>
              
              <View style={styles.goalProgressContainer}>
                <View style={styles.goalProgressBar}>
                  <View 
                    style={[
                      styles.goalProgressFill, 
                      { width: `${(goal.progress / goal.target) * 100}%` }
                    ]} 
                  />
                </View>
                <Text style={styles.goalProgressText}>
                  {Math.round((goal.progress / goal.target) * 100)}%
                </Text>
              </View>
              
              <View style={styles.goalFooter}>
                <View style={styles.goalMetadata}>
                  <Text style={styles.goalCategory}>
                    {goal.category.replace('_', ' ').toUpperCase()}
                  </Text>
                  <Text style={styles.goalType}>
                    {goal.type.replace('_', ' ')}
                  </Text>
                </View>
                
                {goal.targetDate && (
                  <Text style={styles.goalTargetDate}>
                    Due: {new Date(goal.targetDate).toLocaleDateString()}
                  </Text>
                )}
              </View>
              
              {goal.milestones && goal.milestones.length > 0 && (
                <View style={styles.milestonesContainer}>
                  <Text style={styles.milestonesTitle}>Milestones:</Text>
                  {goal.milestones.map((milestone, index) => (
                    <View key={milestone.id} style={styles.milestoneItem}>
                      <Ionicons 
                        name={milestone.completed ? 'checkmark-circle' : 'ellipse-outline'} 
                        size={16} 
                        color={milestone.completed ? '#27ae60' : '#bdc3c7'} 
                      />
                      <Text style={[
                        styles.milestoneText,
                        milestone.completed && styles.milestoneCompleted
                      ]}>
                        {milestone.title}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </TouchableOpacity>
          ))
        )}
      </View>
    </ScrollView>
  );

  const renderPractice = () => (
    <ScrollView style={styles.tabContent}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Interview Practice</Text>
        
        <TouchableOpacity 
          style={styles.practiceCard}
          onPress={startInterviewPractice}
        >
          <LinearGradient
            colors={['#11998e', '#38ef7d']}
            style={styles.practiceCardGradient}
          >
            <Ionicons name="school" size={32} color="white" />
            <Text style={styles.practiceCardTitle}>Start Mock Interview</Text>
            <Text style={styles.practiceCardDescription}>
              Practice with AI-generated questions tailored to your field
            </Text>
          </LinearGradient>
        </TouchableOpacity>
        
        {/* Interview History */}
        {interviewHistory.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Practice History</Text>
            {interviewHistory.slice(0, 5).map((session, index) => (
              <View key={session.id || index} style={styles.historyCard}>
                <View style={styles.historyHeader}>
                  <Text style={styles.historyDate}>
                    {new Date(session.createdAt).toLocaleDateString()}
                  </Text>
                  <Text style={styles.historyScore}>
                    Score: {session.score || 'N/A'}%
                  </Text>
                </View>
                <Text style={styles.historyQuestions}>
                  {session.questions?.length || 0} questions answered
                </Text>
              </View>
            ))}
          </>
        )}
        
        {/* Question Categories */}
        <Text style={styles.sectionTitle}>Question Categories</Text>
        <View style={styles.categoriesGrid}>
          {[
            { key: 'behavioral', label: 'Behavioral', icon: 'people', color: '#3498db' },
            { key: 'technical', label: 'Technical', icon: 'code-slash', color: '#e74c3c' },
            { key: 'situational', label: 'Situational', icon: 'bulb', color: '#f39c12' },
            { key: 'company_specific', label: 'Company Specific', icon: 'business', color: '#9b59b6' },
          ].map((category) => (
            <TouchableOpacity
              key={category.key}
              style={[styles.categoryCard, { borderColor: category.color }]}
              onPress={() => {
                // Filter questions by category and start practice
                CareerCoachingService.getInterviewQuestions(category.key).then(questions => {
                  setInterviewQuestions(questions);
                  setCurrentQuestion(0);
                  setUserAnswer('');
                  setPracticeMode(true);
                  setShowInterviewModal(true);
                });
              }}
            >
              <Ionicons name={category.icon} size={24} color={category.color} />
              <Text style={styles.categoryLabel}>{category.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderResources = () => (
    <ScrollView style={styles.tabContent}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Learning Resources</Text>
        
        {learningResources.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="library-outline" size={64} color="#bdc3c7" />
            <Text style={styles.emptyStateTitle}>No Resources Available</Text>
            <Text style={styles.emptyStateDescription}>
              Learning resources will appear here as they become available
            </Text>
          </View>
        ) : (
          learningResources.map((resource) => (
            <TouchableOpacity
              key={resource.id}
              style={styles.resourceCard}
              onPress={() => {
                setSelectedResource(resource);
                setShowResourceModal(true);
              }}
            >
              <View style={styles.resourceHeader}>
                <View style={styles.resourceIconContainer}>
                  <Ionicons 
                    name={
                      resource.type === 'guide' ? 'book' :
                      resource.type === 'template' ? 'document-text' :
                      resource.type === 'assessment' ? 'checkmark-circle' :
                      'library'
                    } 
                    size={24} 
                    color="#667eea" 
                  />
                </View>
                <View style={styles.resourceInfo}>
                  <Text style={styles.resourceTitle}>{resource.title}</Text>
                  <Text style={styles.resourceDescription}>{resource.description}</Text>
                </View>
              </View>
              
              <View style={styles.resourceMetadata}>
                <View style={styles.resourceTags}>
                  {resource.tags?.slice(0, 3).map((tag, index) => (
                    <View key={index} style={styles.resourceTag}>
                      <Text style={styles.resourceTagText}>{tag}</Text>
                    </View>
                  ))}
                </View>
                
                <View style={styles.resourceStats}>
                  <Text style={styles.resourceTime}>{resource.estimatedTime}</Text>
                  <View style={styles.resourceRating}>
                    <Ionicons name="star" size={12} color="#f39c12" />
                    <Text style={styles.resourceRatingText}>{resource.rating}</Text>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          ))
        )}
      </View>
    </ScrollView>
  );

  // Modal components
  const renderChatModal = () => (
    <Modal
      visible={showChatModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowChatModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowChatModal(false)}
          >
            <Ionicons name="close" size={24} color="#333" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>AI Career Coach</Text>
          <View style={{ width: 24 }} />
        </View>
        
        <View style={styles.chatContainer}>
          <ScrollView 
            ref={chatScrollRef}
            style={styles.chatMessages}
            showsVerticalScrollIndicator={false}
          >
            {chatMessages.map((message) => (
              <View
                key={message.id}
                style={[
                  styles.messageContainer,
                  message.type === 'user' ? styles.userMessage : styles.aiMessage
                ]}
              >
                <Text style={[
                  styles.messageText,
                  message.type === 'user' ? styles.userMessageText : styles.aiMessageText
                ]}>
                  {message.content}
                </Text>
                
                {message.suggestions && message.suggestions.length > 0 && (
                  <View style={styles.suggestionsContainer}>
                    {message.suggestions.map((suggestion, index) => (
                      <TouchableOpacity
                        key={index}
                        style={styles.suggestionButton}
                        onPress={() => handleSuggestionPress(suggestion)}
                      >
                        <Text style={styles.suggestionText}>{suggestion}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
                
                {message.actionItems && message.actionItems.length > 0 && (
                  <View style={styles.actionItemsContainer}>
                    {message.actionItems.map((actionItem, index) => (
                      <TouchableOpacity
                        key={index}
                        style={styles.actionItemButton}
                        onPress={() => handleActionItemPress(actionItem)}
                      >
                        <Text style={styles.actionItemText}>{actionItem.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            ))}
            
            {isTyping && (
              <View style={[styles.messageContainer, styles.aiMessage]}>
                <View style={styles.typingIndicator}>
                  <Text style={styles.typingText}>AI is typing</Text>
                  <ActivityIndicator size={20} color="#667eea" />
                </View>
              </View>
            )}
          </ScrollView>
          
          <KeyboardAvoidingView 
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.chatInputContainer}
          >
            <TextInput
              style={styles.chatInput}
              value={currentMessage}
              onChangeText={setCurrentMessage}
              placeholder="Type your message..."
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                !currentMessage.trim() && styles.sendButtonDisabled
              ]}
              onPress={() => sendMessage(currentMessage)}
              disabled={!currentMessage.trim() || isTyping}
            >
              <Ionicons name="send" size={20} color="white" />
            </TouchableOpacity>
          </KeyboardAvoidingView>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderGoalModal = () => (
    <Modal
      visible={showGoalModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowGoalModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowGoalModal(false)}
          >
            <Ionicons name="close" size={24} color="#333" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Create New Goal</Text>
          <TouchableOpacity
            style={styles.modalSaveButton}
            onPress={createNewGoal}
          >
            <Text style={styles.modalSaveButtonText}>Save</Text>
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.modalContent}>
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Goal Title *</Text>
            <TextInput
              style={styles.formInput}
              value={newGoalTitle}
              onChangeText={setNewGoalTitle}
              placeholder="Enter your goal title"
              maxLength={100}
            />
          </View>
          
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Description</Text>
            <TextInput
              style={[styles.formInput, styles.formTextArea]}
              value={newGoalDescription}
              onChangeText={setNewGoalDescription}
              placeholder="Describe your goal in detail"
              multiline
              numberOfLines={4}
              maxLength={500}
            />
          </View>
          
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Category</Text>
            <View style={styles.categorySelector}>
              {Object.entries(CareerCoachingService.COACHING_CATEGORIES).map(([key, value]) => (
                <TouchableOpacity
                  key={key}
                  style={[
                    styles.categoryOption,
                    newGoalCategory === value && styles.categoryOptionSelected
                  ]}
                  onPress={() => setNewGoalCategory(value)}
                >
                  <Text style={[
                    styles.categoryOptionText,
                    newGoalCategory === value && styles.categoryOptionTextSelected
                  ]}>
                    {value.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          
          <View style={styles.formGroup}>
            <Text style={styles.formLabel}>Timeline</Text>
            <View style={styles.timelineSelector}>
              {Object.entries(CareerCoachingService.GOAL_TYPES).map(([key, value]) => (
                <TouchableOpacity
                  key={key}
                  style={[
                    styles.timelineOption,
                    newGoalType === value && styles.timelineOptionSelected
                  ]}
                  onPress={() => setNewGoalType(value)}
                >
                  <Text style={[
                    styles.timelineOptionText,
                    newGoalType === value && styles.timelineOptionTextSelected
                  ]}>
                    {value.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Text>
                  <Text style={styles.timelineOptionDescription}>
                    {value === 'short_term' ? '1-3 months' :
                     value === 'medium_term' ? '3-12 months' : '1+ years'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderInterviewModal = () => (
    <Modal
      visible={showInterviewModal}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={() => setShowInterviewModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => {
              setPracticeMode(false);
              setShowInterviewModal(false);
            }}
          >
            <Ionicons name="close" size={24} color="#333" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Interview Practice</Text>
          <Text style={styles.questionCounter}>
            {currentQuestion + 1} / {interviewQuestions.length}
          </Text>
        </View>
        
        {practiceMode && interviewQuestions.length > 0 && (
          <View style={styles.interviewContainer}>
            <ScrollView style={styles.interviewContent}>
              <View style={styles.questionCard}>
                <Text style={styles.questionNumber}>
                  Question {currentQuestion + 1}
                </Text>
                <Text style={styles.questionText}>
                  {interviewQuestions[currentQuestion]?.question}
                </Text>
                
                {interviewQuestions[currentQuestion]?.tips && (
                  <View style={styles.tipsContainer}>
                    <Ionicons name="bulb" size={16} color="#f39c12" />
                    <Text style={styles.tipsText}>
                      {interviewQuestions[currentQuestion].tips}
                    </Text>
                  </View>
                )}
              </View>
              
              <View style={styles.answerContainer}>
                <Text style={styles.answerLabel}>Your Answer:</Text>
                <TextInput
                  style={styles.answerInput}
                  value={userAnswer}
                  onChangeText={setUserAnswer}
                  placeholder="Type your answer here..."
                  multiline
                  numberOfLines={8}
                  textAlignVertical="top"
                />
              </View>
            </ScrollView>
            
            <View style={styles.interviewActions}>
              <TouchableOpacity
                style={[
                  styles.submitAnswerButton,
                  !userAnswer.trim() && styles.submitAnswerButtonDisabled
                ]}
                onPress={submitAnswer}
                disabled={!userAnswer.trim()}
              >
                <Text style={styles.submitAnswerButtonText}>
                  {currentQuestion < interviewQuestions.length - 1 ? 'Next Question' : 'Finish Practice'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderProgressModal = () => (
    <Modal
      visible={showProgressModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowProgressModal(false)}
    >
      {selectedGoal && (
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowProgressModal(false)}
            >
              <Ionicons name="close" size={24} color="#333" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Goal Progress</Text>
            <View style={{ width: 24 }} />
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.goalDetailCard}>
              <Text style={styles.goalDetailTitle}>{selectedGoal.title}</Text>
              <Text style={styles.goalDetailDescription}>{selectedGoal.description}</Text>
              
              <View style={styles.goalDetailProgress}>
                <View style={styles.goalDetailProgressBar}>
                  <View 
                    style={[
                      styles.goalDetailProgressFill, 
                      { width: `${(selectedGoal.progress / selectedGoal.target) * 100}%` }
                    ]} 
                  />
                </View>
                <Text style={styles.goalDetailProgressText}>
                  {Math.round((selectedGoal.progress / selectedGoal.target) * 100)}% Complete
                </Text>
              </View>
              
              <View style={styles.goalDetailMeta}>
                <Text style={styles.goalDetailCategory}>
                  {selectedGoal.category.replace('_', ' ').toUpperCase()}
                </Text>
                <Text style={styles.goalDetailType}>
                  {selectedGoal.type.replace('_', ' ')}
                </Text>
              </View>
            </View>
            
            {selectedGoal.milestones && selectedGoal.milestones.length > 0 && (
              <View style={styles.milestonesSection}>
                <Text style={styles.milestonesSectionTitle}>Milestones</Text>
                {selectedGoal.milestones.map((milestone) => (
                  <TouchableOpacity
                    key={milestone.id}
                    style={styles.milestoneDetailItem}
                    onPress={() => {
                      // Toggle milestone completion
                      const updatedMilestones = selectedGoal.milestones.map(m =>
                        m.id === milestone.id ? { ...m, completed: !m.completed } : m
                      );
                      
                      const completedCount = updatedMilestones.filter(m => m.completed).length;
                      const newProgress = (completedCount / updatedMilestones.length) * 100;
                      
                      updateGoalProgress(selectedGoal.id, newProgress);
                      setShowProgressModal(false);
                    }}
                  >
                    <Ionicons 
                      name={milestone.completed ? 'checkmark-circle' : 'ellipse-outline'} 
                      size={20} 
                      color={milestone.completed ? '#27ae60' : '#bdc3c7'} 
                    />
                    <Text style={[
                      styles.milestoneDetailText,
                      milestone.completed && styles.milestoneDetailCompleted
                    ]}>
                      {milestone.title}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </ScrollView>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderResourceModal = () => (
    <Modal
      visible={showResourceModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowResourceModal(false)}
    >
      {selectedResource && (
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowResourceModal(false)}
            >
              <Ionicons name="close" size={24} color="#333" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Resource Details</Text>
            <View style={{ width: 24 }} />
          </View>
          
          <ScrollView style={styles.modalContent}>
            <View style={styles.resourceDetailCard}>
              <View style={styles.resourceDetailHeader}>
                <Ionicons 
                  name={
                    selectedResource.type === 'guide' ? 'book' :
                    selectedResource.type === 'template' ? 'document-text' :
                    selectedResource.type === 'assessment' ? 'checkmark-circle' :
                    'library'
                  } 
                  size={32} 
                  color="#667eea" 
                />
                <View style={styles.resourceDetailInfo}>
                  <Text style={styles.resourceDetailTitle}>{selectedResource.title}</Text>
                  <Text style={styles.resourceDetailType}>{String(selectedResource.type ?? '').toUpperCase()}</Text>
                </View>
              </View>
              
              <Text style={styles.resourceDetailDescription}>{selectedResource.description}</Text>
              
              <View style={styles.resourceDetailMeta}>
                <View style={styles.resourceDetailMetaItem}>
                  <Ionicons name="time" size={16} color="#666" />
                  <Text style={styles.resourceDetailMetaText}>{selectedResource.estimatedTime}</Text>
                </View>
                
                <View style={styles.resourceDetailMetaItem}>
                  <Ionicons name="bar-chart" size={16} color="#666" />
                  <Text style={styles.resourceDetailMetaText}>{selectedResource.difficulty}</Text>
                </View>
                
                <View style={styles.resourceDetailMetaItem}>
                  <Ionicons name="star" size={16} color="#f39c12" />
                  <Text style={styles.resourceDetailMetaText}>{selectedResource.rating}</Text>
                </View>
              </View>
              
              {selectedResource.tags && selectedResource.tags.length > 0 && (
                <View style={styles.resourceDetailTags}>
                  {selectedResource.tags.map((tag, index) => (
                    <View key={index} style={styles.resourceDetailTag}>
                      <Text style={styles.resourceDetailTagText}>{tag}</Text>
                    </View>
                  ))}
                </View>
              )}
              
              <TouchableOpacity 
                style={styles.resourceDetailButton}
                onPress={() => {
                  // Handle resource access
                  Alert.alert('Resource Access', 'This feature will be implemented to open the resource.');
                  setShowResourceModal(false);
                }}
              >
                <Text style={styles.resourceDetailButtonText}>Access Resource</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </SafeAreaView>
      )}
    </Modal>
  );

  // Loading screen
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#667eea" />
        <Text style={styles.loadingText}>Initializing your career coach...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderTabs()}
      
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'goals' && renderGoals()}
      {activeTab === 'practice' && renderPractice()}
      {activeTab === 'resources' && renderResources()}

      {renderChatModal()}
      {renderGoalModal()}
      {renderInterviewModal()}
      {renderProgressModal()}
      {renderResourceModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f6fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f6fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    paddingTop: 44,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    marginRight: 16,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  chatButton: {
    padding: 8,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: 'white',
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#667eea',
  },
  tabText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 8,
    fontWeight: '500',
  },
  activeTabText: {
    color: '#667eea',
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
  section: {
    backgroundColor: 'white',
    marginBottom: 12,
    padding: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  seeAllText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
  },
  addButton: {
    backgroundColor: '#667eea',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  analyticsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  analyticsCard: {
    width: '50%',
    paddingHorizontal: 8,
    marginBottom: 16,
  },
  analyticsIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f0f8ff',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  analyticsNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  analyticsLabel: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  quickActionCard: {
    width: '50%',
    paddingHorizontal: 8,
    marginBottom: 16,
  },
  quickActionGradient: {
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 100,
  },
  quickActionText: {
    fontSize: 14,
    color: 'white',
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 8,
  },
  goalCard: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#667eea',
  },
  goalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  goalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
    marginRight: 12,
  },
  goalProgress: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
  },
  goalDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  goalProgressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  goalProgressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#e0e6ed',
    borderRadius: 3,
    marginRight: 12,
  },
  goalProgressFill: {
    height: '100%',
    backgroundColor: '#667eea',
    borderRadius: 3,
  },
  goalProgressText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600',
  },
  goalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  goalMetadata: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  goalCategory: {
    fontSize: 10,
    color: '#667eea',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginRight: 8,
    fontWeight: '600',
  },
  goalType: {
    fontSize: 10,
    color: '#666',
    textTransform: 'capitalize',
  },
  goalTargetDate: {
    fontSize: 12,
    color: '#666',
  },
  goalStatusBadge: {
    backgroundColor: '#27ae60',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  goalStatusText: {
    fontSize: 10,
    color: 'white',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  milestonesContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e6ed',
  },
  milestonesTitle: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600',
    marginBottom: 8,
  },
  milestoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  milestoneText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 8,
  },
  milestoneCompleted: {
    textDecorationLine: 'line-through',
    color: '#bdc3c7',
  },
  recommendationCard: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  recommendationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginLeft: 8,
  },
  recommendationDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  recommendationButton: {
    backgroundColor: '#3498db',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  recommendationButtonText: {
    fontSize: 12,
    color: 'white',
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  emptyStateButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyStateButtonText: {
    fontSize: 14,
    color: 'white',
    fontWeight: '600',
  },
  practiceCard: {
    marginBottom: 20,
    borderRadius: 12,
    overflow: 'hidden',
  },
  practiceCardGradient: {
    padding: 24,
    alignItems: 'center',
  },
  practiceCardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    marginTop: 12,
    marginBottom: 8,
  },
  practiceCardDescription: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: 20,
  },
  historyCard: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  historyDate: {
    fontSize: 14,
    color: '#333',
    fontWeight: '600',
  },
  historyScore: {
    fontSize: 14,
    color: '#27ae60',
    fontWeight: '600',
  },
  historyQuestions: {
    fontSize: 12,
    color: '#666',
  },
  categoriesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  categoryCard: {
    width: '50%',
    paddingHorizontal: 8,
    marginBottom: 16,
  },
  categoryLabel: {
    fontSize: 12,
    color: '#333',
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 8,
  },
  resourceCard: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  resourceHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  resourceIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f0f8ff',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  resourceInfo: {
    flex: 1,
  },
  resourceTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  resourceDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  resourceMetadata: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resourceTags: {
    flexDirection: 'row',
    flex: 1,
  },
  resourceTag: {
    backgroundColor: '#e0e6ed',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginRight: 4,
  },
  resourceTagText: {
    fontSize: 10,
    color: '#666',
    fontWeight: '500',
  },
  resourceStats: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resourceTime: {
    fontSize: 12,
    color: '#666',
    marginRight: 12,
  },
  resourceRating: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resourceRatingText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 2,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'white',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  modalSaveButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  modalSaveButtonText: {
    fontSize: 14,
    color: 'white',
    fontWeight: '600',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  chatContainer: {
    flex: 1,
  },
  chatMessages: {
    flex: 1,
    paddingHorizontal: 20,
  },
  messageContainer: {
    marginVertical: 8,
    maxWidth: '80%',
  },
  userMessage: {
    alignSelf: 'flex-end',
  },
  aiMessage: {
    alignSelf: 'flex-start',
  },
  messageText: {
    fontSize: 16,
    lineHeight: 22,
    padding: 12,
    borderRadius: 16,
  },
  userMessageText: {
    backgroundColor: '#667eea',
    color: 'white',
  },
  aiMessageText: {
    backgroundColor: '#f0f0f0',
    color: '#333',
  },
  suggestionsContainer: {
    marginTop: 8,
  },
  suggestionButton: {
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: '#667eea',
  },
  suggestionText: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '500',
  },
  actionItemsContainer: {
    marginTop: 8,
  },
  actionItemButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginBottom: 4,
  },
  actionItemText: {
    fontSize: 12,
    color: 'white',
    fontWeight: '600',
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 16,
  },
  typingText: {
    fontSize: 14,
    color: '#666',
    marginRight: 8,
  },
  chatInputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  chatInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    maxHeight: 100,
    marginRight: 12,
  },
  sendButton: {
    backgroundColor: '#667eea',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#bdc3c7',
  },
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: 'white',
  },
  formTextArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  categorySelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -4,
  },
  categoryOption: {
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    margin: 4,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  categoryOptionSelected: {
    backgroundColor: '#f0f8ff',
    borderColor: '#667eea',
  },
  categoryOptionText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  categoryOptionTextSelected: {
    color: '#667eea',
    fontWeight: '600',
  },
  timelineSelector: {
    marginTop: 8,
  },
  timelineOption: {
    backgroundColor: '#f0f0f0',
    padding: 16,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  timelineOptionSelected: {
    backgroundColor: '#f0f8ff',
    borderColor: '#667eea',
  },
  timelineOptionText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '600',
    marginBottom: 4,
  },
  timelineOptionTextSelected: {
    color: '#667eea',
  },
  timelineOptionDescription: {
    fontSize: 12,
    color: '#666',
  },
  questionCounter: {
    fontSize: 14,
    color: '#666',
    fontWeight: '600',
  },
  interviewContainer: {
    flex: 1,
  },
  interviewContent: {
    flex: 1,
    padding: 20,
  },
  questionCard: {
    backgroundColor: '#f8f9fa',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
  },
  questionNumber: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  questionText: {
    fontSize: 18,
    color: '#333',
    fontWeight: '600',
    lineHeight: 26,
    marginBottom: 16,
  },
  tipsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fff8e1',
    padding: 12,
    borderRadius: 8,
  },
  tipsText: {
    fontSize: 14,
    color: '#f57f17',
    marginLeft: 8,
    flex: 1,
    lineHeight: 20,
  },
  answerContainer: {
    flex: 1,
  },
  answerLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  answerInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 16,
    fontSize: 16,
    textAlignVertical: 'top',
    backgroundColor: 'white',
  },
  interviewActions: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  submitAnswerButton: {
    backgroundColor: '#667eea',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitAnswerButtonDisabled: {
    backgroundColor: '#bdc3c7',
  },
  submitAnswerButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: '600',
  },
  goalDetailCard: {
    backgroundColor: '#f8f9fa',
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
  },
  goalDetailTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  goalDetailDescription: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    marginBottom: 20,
  },
  goalDetailProgress: {
    marginBottom: 20,
  },
  goalDetailProgressBar: {
    height: 8,
    backgroundColor: '#e0e6ed',
    borderRadius: 4,
    marginBottom: 8,
  },
  goalDetailProgressFill: {
    height: '100%',
    backgroundColor: '#667eea',
    borderRadius: 4,
  },
  goalDetailProgressText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
    textAlign: 'center',
  },
  goalDetailMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  goalDetailCategory: {
    fontSize: 12,
    color: '#667eea',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    fontWeight: '600',
  },
  goalDetailType: {
    fontSize: 12,
    color: '#666',
    textTransform: 'capitalize',
  },
  milestonesSection: {
    backgroundColor: '#f8f9fa',
    padding: 20,
    borderRadius: 12,
  },
  milestonesSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  milestoneDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e6ed',
  },
  milestoneDetailText: {
    fontSize: 14,
    color: '#333',
    marginLeft: 12,
    flex: 1,
  },
  milestoneDetailCompleted: {
    textDecorationLine: 'line-through',
    color: '#bdc3c7',
  },
  resourceDetailCard: {
    backgroundColor: '#f8f9fa',
    padding: 20,
    borderRadius: 12,
  },
  resourceDetailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  resourceDetailInfo: {
    marginLeft: 16,
    flex: 1,
  },
  resourceDetailTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  resourceDetailType: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600',
  },
  resourceDetailDescription: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    marginBottom: 20,
  },
  resourceDetailMeta: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderRadius: 8,
  },
  resourceDetailMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resourceDetailMetaText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 4,
    fontWeight: '500',
  },
  resourceDetailTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  resourceDetailTag: {
    backgroundColor: '#e0e6ed',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  resourceDetailTagText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  resourceDetailButton: {
    backgroundColor: '#667eea',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  resourceDetailButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: '600',
  },
});